import {Component, OnInit} from '@angular/core';
import {
  CoreService,
  DocumentService,
  ImageService,
  OrdersService,
  Param,
  ProleisObject,
  Types
} from '@proleis/proleis-rest-client';
import {ActivatedRoute, Params} from '@angular/router';
import {combineLatest, iif, Observable, of} from 'rxjs';
import {debounceTime, exhaustMap, startWith, tap} from 'rxjs/operators';
import {saveAs} from 'file-saver';
import {FormControl} from '@angular/forms';
import {CurrentViewService, NotificationService} from '@proleis/proleis-web-app';
import {SuppliersService} from '../../services/suppliers.service';
import {FileResponse} from '../../models/file.response';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {CreateDeliveryNoteComponent} from './overlays/create-delivery-note/create.delivery.note.component';

@Component({
  selector: 'ps-delivery-notes',
  templateUrl: 'delivery.notes.list.component.html'
})
export class DeliveryNotesListComponent implements OnInit {

  guidDelivered = '8f999f30-2925-11b2-8055-278188925e2c';

  pbo$: Observable<ProleisObject>;
  pbo: ProleisObject;
  pboStatusIcons: Map<string, string>;
  position$: Observable<ProleisObject>;
  pos: ProleisObject;
  deliveryNotes$: Observable<ProleisObject[]>;
  searchFormControl: FormControl;
  icons: Map<string, string>;

  constructor(private activatedRoute: ActivatedRoute, private coreService: CoreService,
              private imageService: ImageService, private cvs: CurrentViewService, private suppliersService: SuppliersService,
              private ordersService: OrdersService, private notificationService: NotificationService,
              private documentsService: DocumentService, private bottomSheet: MatBottomSheet,
              private currentViewService: CurrentViewService) {
    this.icons = new Map();
  }

  ngOnInit() {
    this.searchFormControl = new FormControl();

    const params = this.activatedRoute.snapshot.params;
    const aliasFields = 'LIEFERANT=FIRMA_ID.NAME;LIEFERSTATUS=FREIGABE_ID.DESCRIPTION;LIEFERSTATUS_ICON=FREIGABE_ID.ICON;' +
      'PROJEKTNAME=LINK_ID.PARENTANDMYSELF[TYP_ID.GUID=b514d7ec-27fa-11b2-800a-b390cd5bbe84].TEXT;CHANNELGUID=CHANNEL_ID.GUID';
    this.pbo$ = this.ordersService.getPurchaseOrder(params.id, aliasFields);
    this.pbo$.subscribe(pbo => this.pbo = pbo);
    this.pbo$.subscribe(pbo => {
      this.imageService.getIconUrls(pbo.LIEFERSTATUS_ICON).subscribe(icons => {
        this.pboStatusIcons = icons;
      });
    });

    this.position$ = this.suppliersService.getPosition(params.id, params.posId);
    this.position$.subscribe(pos => this.pos = pos);
    this.deliveryNotes$ = this.loadDeliveryNotes(params);
    this.updateDeliveryNotes();
  }


  updateDeliveryNotes() {
    this.deliveryNotes$ = combineLatest(
      [this.deliveryNotes$, this.filter()]
    ).pipe(
      exhaustMap(([deliveryNotes, filterExpression]) => {
        return iif(() => filterExpression === '',
          of(deliveryNotes),
          of(deliveryNotes.filter(note => note.NAME.includes(filterExpression) || note.DELIVERYNOTE.includes(filterExpression))));
      }),
      tap(result => this.cvs.setLoading(false))
    );
  }

  loadDeliveryNotes(params: Params): Observable<ProleisObject[]> {
    this.cvs.setLoading(true);
    const aliasFields = 'DOKUMENT=DE_DOC_ID.NAME;FREIGABE=FREIGABE_ID.DESCRIPTION;FREIGABE_ICON=FREIGABE_ID.CLIENTICON' +
      ';FREIGABE_GUID=FREIGABE_ID.GUID';
    return this.position$.pipe(
      exhaustMap(pos => {
        return this.suppliersService.getDeliveryNotes(params.id, params.posId, null, aliasFields).pipe(
          tap(notes => {
            if (notes && notes.length > 0) {
              const iconIdentifiers: string[] = notes.map(note => note.FREIGABE_ICON);
              const joinedIdentifiers: string = iconIdentifiers.join(',');
              this.imageService.getIconUrls(joinedIdentifiers).subscribe(icons => {
                this.icons = new Map([...this.icons, ...Object.entries(icons)]);
              });
            }
          })
        );
      })
    );
  }

  downloadDeliveryNote(deliveryNote: ProleisObject) {
    this.cvs.setLoading(true);
    if (deliveryNote.DE_DOC_ID) {
      return this.documentsService.getDocumentFile(deliveryNote.DE_DOC_ID).subscribe(response => {
          const fileResponse: FileResponse = FileResponse.parseResponse(response);
          saveAs(fileResponse.content, fileResponse.fileName);
          this.cvs.setLoading(false);
        }
      );
    } else {
      this.notificationService.showNotification('Kein Dokument vorhanden');
      this.cvs.setLoading(false);
      return of(null);
    }
  }

  cancelDelivery(noteId: string) {
    this.coreService.executeAction(noteId, '§3e1ad372-2c90-11b2-8008-9dbbf888e821', null).subscribe(result => {
      this.updateDeliveryNotes();
    });
  }

  filter(): Observable<string> {
    return this.searchFormControl.valueChanges.pipe(
      tap(value => this.cvs.setLoading(true)),
      debounceTime(250),
      startWith('')
    );
  }

  createDeliveryNote() {
    this.position$.subscribe(pos => {
      this.bottomSheet.open(CreateDeliveryNoteComponent).afterDismissed().subscribe(bottomSheetData => {
        if (bottomSheetData) {
          const params: Param[] = [
            {name: 'STEP_INFO_TEXT', value: bottomSheetData.deliveryNr, type: Types.TEXT},
            {name: 'STEP_INFO_TEXT_COUNT', value: bottomSheetData.count, type: Types.DOUBLE},
            {name: 'STEP_INFO_MODE', value: bottomSheetData.replacement, type: Types.BOOL},
          ];
          this.currentViewService.setLoading(true);
          // 32247448-2c49-11b2-80ce-5b47e4bd1ab4 = SCRIPT_PSE_CREATE_DELIVERYNOTE_EXTERN
          this.coreService.executeAction<any>(pos.OBJECT_ID, '§32247448-2c49-11b2-80ce-5b47e4bd1ab4', params).subscribe(result => {
            this.suppliersService.getDeliveryNote(result.value, 'DE_DOC_ID=DE_DOC_ID').subscribe(note => {
              this.downloadDeliveryNote(note);
              this.currentViewService.setLoading(false);
              this.notificationService.showNotification('Lieferschein angelegt');
              this.updateDeliveryNotes();
            });
          });
        }
      });
    });
  }
}
